import type { AuthenticationResponseJSON, PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON, RegistrationResponseJSON } from '@simplewebauthn/browser'
import type { ContentCollection, LibrarySyncMutation, LibrarySyncState, MediaDetail, MediaSummary, PagedResult, SourceCategory, SourceConfig, SourcePublic } from '../../shared/contracts'
import type { WatchMedia, WatchPlaybackState, WatchSessionSnapshot, WatchUsageStatus, WatchUserDirectoryPage } from '../../shared/watch-together'

const API_BASE = (import.meta.env.VITE_API_BASE || '/api').replace(/\/$/u, '')

export class ApiError extends Error {
    constructor(message: string, public status: number) {
        super(message)
    }
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
    let response: Response
    try {
        response = await fetch(`${API_BASE}${path}`, {
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', ...options?.headers },
            ...options,
        })
    }
    catch {
        throw new ApiError('网络连接失败，请检查网络后重试', 0)
    }
    const body = await response.json().catch(() => ({})) as { error?: string }
    if (!response.ok) { throw new ApiError(body.error || `请求失败 (${response.status})`, response.status) }
    return body as T
}

export function apiWebSocketUrl(path: string): string {
    const url = new URL(`${API_BASE}${path}`, window.location.href)
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
    return url.href
}

export interface PublicConfig {
    name: string
    database: boolean
    authenticated: boolean
    setupRequired: boolean
    user: AuthUser | null
    authMethod: AuthMethod | null
    authenticatedAt: number | null
    sources: SourcePublic[]
}

export type AuthMethod = 'bootstrap' | 'password' | 'invite' | 'passkey' | 'recovery'

export interface AuthUser {
    id: string
    username: string
    displayName: string
    role: 'owner' | 'member'
    avatar?: string
}

export interface AuthResponse {
    authenticated: boolean
    user: AuthUser
    authMethod: AuthMethod
    authenticatedAt: number
}

export interface PasskeySummary {
    id: string
    deviceName: string
    deviceType: string
    backedUp: boolean
    createdAt: number
    lastUsedAt: number | null
}

export interface UserSummary extends AuthUser {
    disabledAt: number | null
    createdAt: number
}

export interface InviteSummary {
    id: string
    label: string
    userId: string | null
    expiresAt: number
    usedAt: number | null
    createdAt: number
}

export const api = {
    config: () => request<PublicConfig>('/config'),
    discover: (params: { page?: number, source?: string, kind?: string, category?: string } = {}) => {
        const query = new URLSearchParams()
        if (params.page) { query.set('page', String(params.page)) }
        if (params.source) { query.set('source', params.source) }
        if (params.kind && params.kind !== 'all') { query.set('kind', params.kind) }
        if (params.category) { query.set('category', params.category) }
        return request<PagedResult<MediaSummary>>(`/discover?${query}`)
    },
    discoverSources: (sources: SourceConfig[], params: { page?: number, kind?: string, category?: string } = {}) => request<PagedResult<MediaSummary>>('/content/discover', { method: 'POST', body: JSON.stringify({ sources, ...params }) }),
    categories: (source: string) => request<{ categories: SourceCategory[] }>(`/categories/${encodeURIComponent(source)}`),
    categoriesFromSource: (source: SourceConfig) => request<{ categories: SourceCategory[] }>('/content/categories', { method: 'POST', body: JSON.stringify({ source }) }),
    search: (queryText: string, source?: string, page = 1) => {
        const query = new URLSearchParams({ q: queryText, page: String(page) })
        if (source) { query.set('source', source) }
        return request<PagedResult<MediaSummary>>(`/search?${query}`)
    },
    searchSources: (query: string, sources: SourceConfig[], page = 1) => request<PagedResult<MediaSummary>>('/content/search', { method: 'POST', body: JSON.stringify({ query, sources, page }) }),
    detail: (source: string, id: string) => request<MediaDetail>(`/detail/${encodeURIComponent(source)}/${encodeURIComponent(id)}`),
    detailFromSource: (source: SourceConfig, id: string) => request<MediaDetail>('/content/detail', { method: 'POST', body: JSON.stringify({ source, id }) }),
    bootstrap: (payload: { bootstrapPassword: string, username: string, displayName: string, password: string }) => request<AuthResponse>('/auth/bootstrap', { method: 'POST', body: JSON.stringify(payload) }),
    passwordLogin: (username: string, password: string) => request<AuthResponse>('/auth/login/password', { method: 'POST', body: JSON.stringify({ username, password }) }),
    recoverAccount: (code: string, password: string) => request<AuthResponse>('/auth/invite/recover', { method: 'POST', body: JSON.stringify({ code, password }) }),
    claimInvite: (payload: { code: string, username: string, displayName: string, password: string }) => request<AuthResponse>('/auth/invite/claim', { method: 'POST', body: JSON.stringify(payload) }),
    passkeyOptions: () => request<{ challengeId: string, options: PublicKeyCredentialRequestOptionsJSON }>('/auth/passkey/options', { method: 'POST' }),
    verifyPasskey: (challengeId: string, response: AuthenticationResponseJSON) => request<AuthResponse>('/auth/passkey/verify', { method: 'POST', body: JSON.stringify({ challengeId, response }) }),
    passkeyRegistrationOptions: () => request<{ challengeId: string, options: PublicKeyCredentialCreationOptionsJSON }>('/auth/passkeys/register/options', { method: 'POST' }),
    verifyPasskeyRegistration: (challengeId: string, response: RegistrationResponseJSON, deviceName: string) => request<{ ok: boolean }>('/auth/passkeys/register/verify', { method: 'POST', body: JSON.stringify({ challengeId, response, deviceName }) }),
    passkeys: () => request<{ passkeys: PasskeySummary[] }>('/auth/passkeys'),
    deletePasskey: (id: string) => request<{ ok: boolean }>(`/auth/passkeys/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    users: () => request<{ users: UserSummary[], invites: InviteSummary[] }>('/auth/users'),
    createInvite: (payload: { label?: string, userId?: string, expiresInDays?: number }) => request<{ invite: InviteSummary & { code: string } }>('/auth/invites', { method: 'POST', body: JSON.stringify(payload) }),
    deleteInvite: (id: string) => request<{ ok: boolean }>(`/auth/invites/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    setUserDisabled: (id: string, disabled: boolean) => request<{ ok: boolean }>(`/auth/users/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ disabled }) }),
    deleteUser: (id: string) => request<{ ok: boolean }>(`/auth/users/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    changePassword: (currentPassword: string, newPassword: string) => request<{ ok: boolean }>('/auth/password', { method: 'PUT', body: JSON.stringify({ currentPassword, newPassword }) }),
    updateProfile: (payload: { displayName?: string, avatar?: string }) => request<{ user: AuthUser }>('/auth/profile', { method: 'PUT', body: JSON.stringify(payload) }),
    logout: () => request<{ authenticated: boolean }>('/auth/logout', { method: 'POST' }),
    collections: () => request<{ collections: ContentCollection[], database: boolean }>('/content-collections'),
    importCollection: (payload: { id: string, name: string, url: string }) => request<{ collection: ContentCollection }>('/content-collections/import', { method: 'POST', body: JSON.stringify(payload) }),
    setCollectionSourceEnabled: (collectionId: string, sourceId: string, enabled: boolean) => request<{ collection: ContentCollection }>(`/content-collections/${encodeURIComponent(collectionId)}/sources/${encodeURIComponent(sourceId)}`, { method: 'PUT', body: JSON.stringify({ enabled }) }),
    deleteCollection: (id: string) => request<{ ok: boolean }>(`/content-collections/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    getSync: () => request<LibrarySyncState & { database: boolean }>('/sync'),
    putSync: (mutations: LibrarySyncMutation[]) => request<LibrarySyncState>('/sync', { method: 'PUT', body: JSON.stringify({ mutations }) }),
    watchUsers: (query = '', page = 1) => {
        const params = new URLSearchParams({ page: String(page) })
        if (query) { params.set('q', query) }
        return request<WatchUserDirectoryPage>(`/watch/users?${params}`)
    },
    createWatchSession: (payload: { media: WatchMedia, playback: Partial<WatchPlaybackState>, userIds: string[], creationId: string }) => request<{ session: WatchSessionSnapshot }>('/watch/sessions', { method: 'POST', body: JSON.stringify(payload) }),
    watchSession: (id: string) => request<{ session: WatchSessionSnapshot }>(`/watch/sessions/${encodeURIComponent(id)}`),
    watchSessionMedia: (id: string) => request<MediaDetail>(`/watch/sessions/${encodeURIComponent(id)}/media`),
    acceptWatchInvite: (id: string) => request<{ session: WatchSessionSnapshot }>(`/watch/sessions/${encodeURIComponent(id)}/accept`, { method: 'POST' }),
    rejectWatchInvite: (id: string) => request<{ ok: boolean }>(`/watch/sessions/${encodeURIComponent(id)}/reject`, { method: 'POST' }),
    inviteWatchUsers: (id: string, userIds: string[]) => request<{ session: WatchSessionSnapshot, invited: number }>(`/watch/sessions/${encodeURIComponent(id)}/invite`, { method: 'POST', body: JSON.stringify({ userIds }) }),
    kickWatchUser: (id: string, userId: string) => request<{ session: WatchSessionSnapshot }>(`/watch/sessions/${encodeURIComponent(id)}/kick`, { method: 'POST', body: JSON.stringify({ userId }) }),
    leaveWatchSession: (id: string) => request<{ ended: boolean }>(`/watch/sessions/${encodeURIComponent(id)}/leave`, { method: 'POST' }),
    endWatchSession: (id: string, reason?: string) => request<{ ok: boolean }>(`/watch/sessions/${encodeURIComponent(id)}/end`, { method: 'POST', body: JSON.stringify({ reason }) }),
    watchUsage: () => request<WatchUsageStatus>('/watch/usage'),
    toggleWatchService: (enabled: boolean) => request<WatchUsageStatus>('/watch/usage/toggle', { method: 'POST', body: JSON.stringify({ enabled }) }),
}
